import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/use-t';
import { extractApiError } from '@/lib/api-error';
import {
  useDeleteTerminalAudio,
  useTerminalAudio,
  useUploadTerminalAudio,
} from '@/hooks/use-terminal-audio';

/**
 * Manage the sounds a terminal can be pointed at (RL-416).
 *
 * Uploading here does not make anything play. It puts bytes in reach; what a
 * station plays is decided by setting `kitchen.orders.newOrderSoundFile`,
 * which is a normal parameter on this page and picks from this list. Keeping
 * the two apart is what lets one sound serve many stations and lets a station
 * be returned to its built-in tone without deleting a file others use.
 */
export function TerminalAudioPanel({
  onClose,
  canWrite,
}: {
  onClose: () => void;
  canWrite: boolean;
}) {
  const t = useT();
  const assets = useTerminalAudio();
  const upload = useUploadTerminalAudio();
  const remove = useDeleteTerminalAudio();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  async function onPick(file: File | undefined): Promise<void> {
    if (file === undefined) return;
    setError(null);
    try {
      await upload.mutateAsync(file);
      // Let the same file be chosen again after a failure-then-fix.
      if (fileInput.current !== null) fileInput.current.value = '';
    } catch (cause) {
      setError(extractApiError(cause));
    }
  }

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4" aria-label="Terminal sounds">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium">{t('terminalAudio.title', 'Terminal sounds')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'terminalAudio.intro',
              'WAV files a station can play instead of its built-in tone. Uploading one does not change any station — choose it in the “New-order sound file” parameter below.',
            )}
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('terminalAudio.close', 'Close')}
        </Button>
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="terminal-audio-file" className="text-sm font-medium">
            {t('terminalAudio.upload', 'Upload a WAV')}
          </label>
          <input
            ref={fileInput}
            id="terminal-audio-file"
            type="file"
            accept="audio/wav,.wav"
            disabled={upload.isPending}
            className="text-sm"
            onChange={(event) => void onPick(event.target.files?.[0])}
          />
          {upload.isPending && (
            <span className="text-sm text-muted-foreground">
              {t('terminalAudio.uploading', 'Uploading…')}
            </span>
          )}
        </div>
      )}

      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {assets.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('terminalAudio.loading', 'Loading sounds…')}
        </p>
      ) : (assets.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('terminalAudio.empty', 'No sounds uploaded. Stations play their built-in tone.')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {(assets.data ?? []).map((asset) => (
            <li
              key={asset.contentHash}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{asset.originalFilename}</div>
                <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                  {/* Enough of the hash to match against a parameter value
                      without making the row unreadable. */}
                  {asset.contentHash.slice(0, 12)}… · {formatBytes(asset.byteSize)}
                </div>
              </div>
              {canWrite && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={remove.isPending}
                  onClick={async () => {
                    setError(null);
                    try {
                      await remove.mutateAsync(asset.contentHash);
                    } catch (cause) {
                      setError(extractApiError(cause));
                    }
                  }}
                >
                  {t('terminalAudio.delete', 'Delete')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        {t(
          'terminalAudio.deleteNote',
          'Deleting a sound does not change any parameter that still names it — those stations fall back to the built-in tone.',
        )}
      </p>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
